import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

interface WikiArticleSummary {
  title: string
  slug: string
  conclusions: number
  lastCompiled: string
  description?: string
}

interface WikiIndex {
  articles: WikiArticleSummary[]
  totalConclusions: number
  lastUpdated: string
}

export default function WikiIndexPage() {
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<'title' | 'conclusions' | 'date'>('title')

  const { data: wiki, isLoading } = useQuery({
    queryKey: ['wikiIndex'],
    queryFn: async (): Promise<WikiIndex> => {
      const res = await fetch('/api/wiki')
      if (!res.ok) throw new Error('Failed to load wiki')
      return res.json()
    },
  })

  const filteredArticles = useMemo(() => {
    if (!wiki?.articles) return []
    let articles = wiki.articles.filter(a =>
      a.title.toLowerCase().includes(search.toLowerCase())
    )

    if (sortBy === 'conclusions') {
      articles.sort((a, b) => b.conclusions - a.conclusions)
    } else if (sortBy === 'date') {
      articles.sort((a, b) => b.lastCompiled.localeCompare(a.lastCompiled))
    } else {
      articles.sort((a, b) => a.title.localeCompare(b.title))
    }

    return articles
  }, [wiki?.articles, search, sortBy])

  const totalConclusions = wiki?.totalConclusions || 0

  return (
    <div className="wiki-index-page">
      {/* Hero header */}
      <header className="wiki-index-header">
        <div className="wiki-index-header-content">
          <h1 className="wiki-index-title">Knowledge Wiki</h1>
          <p className="wiki-index-subtitle">
            Collective intelligence across all personas — auto-compiled nightly from {totalConclusions.toLocaleString()} conclusions
          </p>

          {/* Search */}
          <div className="wiki-search-container">
            <svg className="wiki-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="wiki-search-input"
              autoFocus
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="wiki-search-clear"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Controls bar */}
      <div className="wiki-controls">
        <span className="wiki-count">
          {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
          {search && ` matching "${search}"`}
        </span>
        <div className="wiki-sort-buttons">
          {(['title', 'conclusions', 'date'] as const).map(s => (
            <button
              key={s}
              onClick={() => setSortBy(s)}
              className={`wiki-sort-btn ${sortBy === s ? 'wiki-sort-active' : ''}`}
            >
              {s === 'title' ? 'A–Z' : s === 'conclusions' ? 'Depth' : 'Recent'}
            </button>
          ))}
        </div>
      </div>

      {/* Article grid */}
      {isLoading ? (
        <div className="wiki-index-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="wiki-card-skeleton" />
          ))}
        </div>
      ) : (
        <div className="wiki-card-grid">
          {filteredArticles.map(article => (
            <Link
              key={article.slug}
              to={`/wiki/${encodeURIComponent(article.slug)}`}
              className="wiki-card"
            >
              <div className="wiki-card-content">
                <h2 className="wiki-card-title">{article.title}</h2>
                {article.description && (
                  <p className="wiki-card-desc">{article.description}</p>
                )}
              </div>
              <div className="wiki-card-footer">
                <span className="wiki-card-conclusions">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                  {article.conclusions}
                </span>
                <span className="wiki-card-date">{formatDate(article.lastCompiled)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredArticles.length === 0 && (
        <div className="wiki-empty">
          <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 8 }}>📚</div>
          <p>No articles found{search ? ` matching "${search}"` : ''}</p>
        </div>
      )}
    </div>
  )
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return dateStr
  }
}
